import React from 'react'
import { Box, Flex, styled, Text } from '@chilipiper/design-system'
import { UnstyledButton } from '@chilipiper/design-system/src/new'
import { addDays, format, startOfDay, startOfWeek, differenceInDays } from '@chilipiper/date-time'
import { getCurrentDateInTimezone } from '@chilipiper/booking-flow'
import { useLocale } from '@chilipiper/locale/src/new'
import { customerColorCss } from './styles'

type Props = {
  availableDays: Record<string, boolean>
  daysWrapperRef: React.RefObject<HTMLDivElement | null>
  onSelectDay: (day: string) => void
  selectedDay?: string
  timezone: string
}

const getWeekDayLabels = (date: Date, locale = 'en_US') => {
  return {
    month: format(date, 'MMM', { locale }),
    weekDay: format(date, 'EEE', { locale }),
    dayNumber: format(date, 'd', { locale }),
  }
}

const TOP_BAR_HEIGHT = 7

export const Days = ({
  daysWrapperRef,
  onSelectDay,
  availableDays,
  selectedDay,
  timezone,
}: Props) => {
  let lastAvailableDay: string | undefined
  for (const day of Object.keys(availableDays)) {
    if (!lastAvailableDay || day > lastAvailableDay) {
      lastAvailableDay = day
    }
  }
  const locale = useLocale()

  const daysToLastAvailableDay = lastAvailableDay
    ? differenceInDays(new Date(lastAvailableDay), new Date(getCurrentDateInTimezone(timezone)))
    : 0

  return (
    <DaysWrapper overflow='auto' ref={daysWrapperRef}>
      <Flex
        flexDirection='column'
        flexShrink={0}
        w={2}
        pt={TOP_BAR_HEIGHT}
        boxShadow='border/row-top'
      >
        <Box flexGrow={1} bg='bg/button-moderate-default' boxShadow='border/row-top' />
      </Flex>
      {Array(Math.max(21, daysToLastAvailableDay + 1) + 6)
        .fill(undefined)
        .map((_, i) => {
          const startOfThisWeek = startOfWeek(new Date())
          const day = addDays(startOfDay(new Date()), i - 6)
          if (day < startOfThisWeek) {
            return null
          }
          const { weekDay, month, dayNumber } = getWeekDayLabels(day, locale)
          const dayString = format(day, 'yyyy-MM-dd')
          const isAvailable = availableDays[dayString]
          return (
            <DayContainer
              key={dayString}
              data-day={dayString}
              w='calc(14.2857% - 2.2857px)' // calc(100% - 16px) / 7
              flexShrink={0}
              boxShadow='border/row-top'
            >
              <Flex h={7} alignItems='center' justifyContent='center'>
                <Text color='fg/moderate' fontSize={12} fontWeight={500}>
                  {weekDay}
                </Text>
              </Flex>

              <DayButton
                boxShadow='border/row-top'
                textAlign='center'
                w='100%'
                py='15px'
                onClick={() => onSelectDay(dayString)}
                disabled={!isAvailable}
                isSelected={dayString === selectedDay}
              >
                <Text color='inherit' fontSize={16} fontWeight={600} display='block' mb={1}>
                  {dayNumber}
                </Text>
                <Text color='inherit' fontSize={12} lineHeight={2} fontWeight={500}>
                  {month}
                </Text>
              </DayButton>
            </DayContainer>
          )
        })}
      <Flex flexDirection='column' flexShrink={0} w={2} pt={TOP_BAR_HEIGHT}>
        <Box flexGrow={1} bg='bg/button-moderate-default' boxShadow='border/row-top' />
      </Flex>
    </DaysWrapper>
  )
}

const DayButton = styled(UnstyledButton)<{ isSelected: boolean }>`
  background: ${({ theme }) => theme.colors['bg/button-moderate-default']};
  color: ${({ theme }) => theme.colors['fg/major']};
  transition:
    background-color 0.2s,
    color 0.2s;

  ${({ isSelected }) => (isSelected ? customerColorCss : undefined)}
  :hover, :focus {
    ${customerColorCss}
  }

  :disabled {
    background: ${({ theme }) => theme.colors['bg/button-moderate-default']};
    color: ${({ theme }) => theme.colors['fg/disabled']};
  }
`

const DaysWrapper = styled(Flex)`
  scroll-snap-type: x mandatory;
  scroll-padding-left: 8px;
  overflow-x: auto;
  overflow-y: hidden;
`

const DayContainer = styled(Box)`
  scroll-snap-align: start;
`
